import type { ChangeEvent } from 'react';
import { Button, Link, Dropdown, DropdownItem } from '@flos/react-ui';
import { useRecoilValue, useRecoilState } from 'recoil';
import { coverableDetailsAtom } from '../../../recoil/atoms/coverableDetailsAtom';
import { sortedCoverablesListSelector } from '../../../recoil/selectors/sortedCoverablesListSelector';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote/';
import { isAgricultureCoverable } from '../../../utils/coverablesUtil';
import { coverableChangesAtom } from '../../../recoil/atoms/coverableChangesAtom';

const CoverableDetailsHeader = ({
    wizardPreviousStep
}: {
    wizardPreviousStep: () => void;
}): React.JSX.Element => {
    const [selectedCoverable, setCoverableDetails] = useRecoilState(coverableDetailsAtom);
    const coverableChanges = useRecoilValue(coverableChangesAtom);

    const sortedPrivateCoverables = useRecoilValue(
        sortedCoverablesListSelector
    ).sortedCoverables.filter(
        (coverable) =>
            !isAgricultureCoverable(coverable) &&
            coverableChanges.filter(
                (cov) =>
                    cov.coverableId === coverable.fixedId && cov.linePrefix === coverable.lobCode
            ).length === 0
    );

    const createUniqueCoverableValue = (coverable: CoverableDTO): string => {
        return `${coverable.lobCode ?? ''}${coverable.fixedId ?? ''}`;
    };

    const changeCoverable = (event: ChangeEvent<HTMLSelectElement>): void => {
        if (event.target.value) {
            const newUniqueCoverableValue = event.target.value;
            const newCoverable = sortedPrivateCoverables.find((coverable) => {
                return createUniqueCoverableValue(coverable) === newUniqueCoverableValue;
            });
            setCoverableDetails(newCoverable);
        }
    };

    return (
        <div className="u-flex-box u-flex-column u-flex-row@xs u-flex-box--space-between u-spacing-stack-xl">
            <div className="u-flex-box u-flex-box--center u-flex-justify-strech u-gap-3">
                <Button
                    theme="secondary"
                    size="medium"
                    iconShape="arrow-left"
                    onClick={wizardPreviousStep}
                />
                <Link className="u-color-primary-2-dark" onClick={wizardPreviousStep}>
                    Tilbage
                </Link>
            </div>
            {selectedCoverable && (
                <Dropdown onChange={changeCoverable}>
                    {sortedPrivateCoverables.map((coverable) => {
                        const uniqueValue = createUniqueCoverableValue(coverable);
                        return (
                            <DropdownItem
                                key={uniqueValue}
                                value={uniqueValue}
                                selected={
                                    createUniqueCoverableValue(selectedCoverable) === uniqueValue
                                }
                            >
                                {`${coverable.flavourName ?? ''} (${coverable.name ?? ''})`}
                            </DropdownItem>
                        );
                    })}
                </Dropdown>
            )}
        </div>
    );
};

export default CoverableDetailsHeader;
